import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

// A tiny breadcrumb trail. The Shell always renders `Group / Section` from the
// active nav item; detail pages (a store, a run, a job) contribute the trailing
// crumb(s) via `useBreadcrumbTail` so the bar reads e.g. `Catalog / Stores / 30995`
// with every step but the last clickable.
//
// The Shell also derives the browser tab title from the same trail (deepest crumb
// first). A page whose deepest crumb is the least useful identifier - a run, where
// the parent job's name says far more than the run id - can pass its own ordered
// title parts instead.

export type Crumb = { label: string; to?: string };

type Trail = { tail: Crumb[]; title: string[] | null };

type Ctx = Trail & { set: (t: Trail) => void };

const EMPTY: Trail = { tail: [], title: null };

const BreadcrumbContext = createContext<Ctx | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<Trail>(EMPTY);
  const set = useCallback((t: Trail) => setTrail(t), []);
  return (
    <BreadcrumbContext.Provider value={{ ...trail, set }}>{children}</BreadcrumbContext.Provider>
  );
}

/** Shell reads the current trailing crumbs here. */
export function useBreadcrumb(): Crumb[] {
  return useContext(BreadcrumbContext)?.tail ?? [];
}

/** Tab-title parts the current page asked for, most-significant first (null = derive). */
export function useBreadcrumbTitle(): string[] | null {
  return useContext(BreadcrumbContext)?.title ?? null;
}

/**
 * Detail pages set their trailing crumb(s); cleared automatically on unmount.
 * `title` overrides the tab title parts the Shell would otherwise derive from
 * the crumbs, and is ordered as it should read (most significant first).
 */
export function useBreadcrumbTail(crumbs: Crumb[], title?: string[]): void {
  const set = useContext(BreadcrumbContext)?.set;
  // key on content so a fresh array identity each render doesn't thrash the effect
  const key = JSON.stringify([crumbs, title ?? null]);
  useEffect(() => {
    const [tail, t] = JSON.parse(key) as [Crumb[], string[] | null];
    set?.({ tail, title: t });
    return () => set?.(EMPTY);
  }, [key, set]);
}
